import type { AxiosError } from "axios";
import { shouldSkipSentryForApiError } from "@/services/api/sentryApiErrorFilter";

export const PULSE_HEARTBEAT_MS = 5 * 60 * 1000;
export const PULSE_ENGAGEMENT_DEBOUNCE_MS = 30_000;
export const PULSE_ENGAGED_WINDOW_MS = 300_000;

const PULSE_API_SAMPLE_WINDOW_MS = 5 * 60 * 1000;
const PULSE_API_SAMPLE_MAX = 6;
const PULSE_API_DEDUP_WINDOW_MS = 2 * 60 * 1000;

const MODIFIER_ONLY_KEYS = new Set([
  "Shift",
  "Control",
  "Alt",
  "Meta",
  "OS",
  "CapsLock",
  "Tab",
]);

/** Paths we never sample for Opulse (pulse loop + health). */
export const PULSE_API_DENYLIST_FRAGMENTS = [
  "/security/user/pulse-heartbeat",
  "/security/user/pulse-error",
  "/security/user/pulse-api-client-error",
  "/health",
  "/uptime/",
] as const;

export type PulseTabVisibility = "visible" | "hidden";

export interface PulseHeartbeatBody {
  route?: string;
  visibility?: PulseTabVisibility;
  engaged?: boolean;
}

export interface PulseApiClientErrorBody {
  route?: string;
  http_status: number | null;
  http_method: string;
  route_group: string;
  error_class: string;
  timeout: boolean;
}

export const isPulseHeartbeatEnrichmentEnabled = (): boolean =>
  import.meta.env.VITE_PULSE_HEARTBEAT_ENRICHMENT === "1";

export const isPulseApiClientErrorsEnabled = (): boolean =>
  import.meta.env.VITE_PULSE_API_CLIENT_ERRORS === "1";

let lastEngagementAt = 0;
let lastEngagementDebounceAt = 0;
let engagementListenersInstalled = false;

let apiSampleTimestamps: number[] = [];
const apiDedupLastSent = new Map<string, number>();

export function resetPulseTelemetryStateForTests(): void {
  lastEngagementAt = 0;
  lastEngagementDebounceAt = 0;
  engagementListenersInstalled = false;
  apiSampleTimestamps = [];
  apiDedupLastSent.clear();
}

export function notePulseEngagement(now = Date.now()): void {
  if (now - lastEngagementDebounceAt < PULSE_ENGAGEMENT_DEBOUNCE_MS) {
    return;
  }
  lastEngagementDebounceAt = now;
  lastEngagementAt = now;
}

export function isPulseEngaged(now = Date.now()): boolean {
  if (!lastEngagementAt) {
    return false;
  }
  return now - lastEngagementAt <= PULSE_ENGAGED_WINDOW_MS;
}

export function buildPulseHeartbeatBody(route?: string): PulseHeartbeatBody {
  const body: PulseHeartbeatBody = {};
  if (route) {
    body.route = route;
  }
  if (!isPulseHeartbeatEnrichmentEnabled()) {
    return body;
  }
  if (typeof document === "undefined") {
    return body;
  }
  const visibility: PulseTabVisibility =
    document.visibilityState === "visible" ? "visible" : "hidden";
  body.visibility = visibility;
  body.engaged = isPulseEngaged();
  return body;
}

export function installPulseEngagementListeners(): void {
  if (engagementListenersInstalled || typeof window === "undefined") {
    return;
  }
  if (!isPulseHeartbeatEnrichmentEnabled()) {
    return;
  }
  engagementListenersInstalled = true;

  const onEngagement = () => {
    if (document.visibilityState !== "visible") {
      return;
    }
    notePulseEngagement();
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (MODIFIER_ONLY_KEYS.has(event.key)) {
      return;
    }
    onEngagement();
  };

  window.addEventListener("click", onEngagement, { passive: true });
  window.addEventListener("keydown", onKeyDown);
  window.addEventListener("scroll", onEngagement, { passive: true });
  window.addEventListener("wheel", onEngagement, { passive: true });
}

function joinUrlPath(baseURL: string | undefined, url: string): string {
  if (!url) {
    return "";
  }
  if (url.startsWith("http://") || url.startsWith("https://")) {
    try {
      return new URL(url).pathname;
    } catch {
      return url;
    }
  }
  const base = (baseURL ?? "/api/v1").replace(/\/+$/, "");
  const path = url.startsWith("/") ? url : `/${url}`;
  if (base.endsWith("/api/v1") || base.includes("/api/v1")) {
    const basePath = base.includes("/api/v1")
      ? base.slice(base.indexOf("/api/v1"))
      : "/api/v1";
    return `${basePath}${path}`.replace(/\/{2,}/g, "/");
  }
  return `${base}${path}`.replace(/\/{2,}/g, "/");
}

/** Collapse request path to `/api/v1/{segment}` for Prom-style grouping. */
export function collapseApiRouteGroup(
  requestUrl: string | undefined,
  baseURL?: string,
): string {
  const pathname = joinUrlPath(baseURL, requestUrl ?? "");
  const match = pathname.match(/\/api\/v1\/([^/?#]+)/);
  if (match) {
    return `/api/v1/${match[1]}`;
  }
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0];
  return first ? `/api/v1/${first}` : "/api/v1/unknown";
}

export function isPulseApiUrlDenied(url: string | undefined): boolean {
  if (!url) {
    return true;
  }
  return PULSE_API_DENYLIST_FRAGMENTS.some((fragment) => url.includes(fragment));
}

function statusOrTimeoutKey(
  httpStatus: number | null,
  timeout: boolean,
  error: AxiosError,
): string {
  if (timeout) {
    if (error.code === "ECONNABORTED") {
      return "timeout";
    }
    return error.code ?? "network";
  }
  return String(httpStatus ?? "unknown");
}

export function tryConsumeApiClientErrorSample(dedupKey: string, now = Date.now()): boolean {
  apiSampleTimestamps = apiSampleTimestamps.filter(
    (t) => now - t < PULSE_API_SAMPLE_WINDOW_MS,
  );
  if (apiSampleTimestamps.length >= PULSE_API_SAMPLE_MAX) {
    return false;
  }
  const lastSent = apiDedupLastSent.get(dedupKey);
  if (lastSent !== undefined && now - lastSent < PULSE_API_DEDUP_WINDOW_MS) {
    return false;
  }
  apiSampleTimestamps.push(now);
  apiDedupLastSent.set(dedupKey, now);
  return true;
}

function classifyNetworkError(error: AxiosError): {
  error_class: string;
  timeout: boolean;
} {
  if (error.code === "ECONNABORTED") {
    return { error_class: "ApiTimeout", timeout: true };
  }
  return { error_class: "ApiNetworkError", timeout: true };
}

/**
 * Build a sampled Opulse api_client_error payload, or null when excluded / over cap.
 */
export function buildSampledApiClientErrorPayload(
  error: AxiosError,
  frontendRoute?: string,
  now = Date.now(),
): PulseApiClientErrorBody | null {
  if (!isPulseApiClientErrorsEnabled()) {
    return null;
  }

  const requestUrl = error.config?.url ?? "";
  if (isPulseApiUrlDenied(requestUrl)) {
    return null;
  }

  if (shouldSkipSentryForApiError(error)) {
    return null;
  }

  const status = error.response?.status;
  if (status === 401 || status === 403) {
    return null;
  }

  const hasResponse = Boolean(error.response);
  const isHttpError = hasResponse && status !== undefined && status >= 400;
  const isNetworkFailure = !hasResponse && Boolean(error.request);

  if (!isHttpError && !isNetworkFailure) {
    return null;
  }

  const method = (error.config?.method ?? "get").toUpperCase().slice(0, 8);
  const route_group = collapseApiRouteGroup(requestUrl, error.config?.baseURL);

  let http_status: number | null = null;
  let error_class = "ApiClientError";
  let timeout = false;

  if (isHttpError && status !== undefined) {
    http_status = status;
  } else {
    const net = classifyNetworkError(error);
    error_class = net.error_class;
    timeout = net.timeout;
  }

  const dedupKey = `${method}:${route_group}:${statusOrTimeoutKey(http_status, timeout, error)}`;
  if (!tryConsumeApiClientErrorSample(dedupKey, now)) {
    return null;
  }

  const route =
    frontendRoute ??
    (typeof window !== "undefined" ? window.location.pathname : undefined);

  return {
    route,
    http_status,
    http_method: method,
    route_group,
    error_class,
    timeout,
  };
}
